import React, { useEffect, useState, useCallback } from "react";
import { IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconButton: {
    padding: 8,
  },
  bell: {
    width: "100%",
    height: "100%",
    transformOrigin: "center top",
    display: "block",
    willChange: "transform",
  },
  ringing: {
    animation: "$ring 0.8s ease-in-out",
  },
  popping: {
    animation: "$pop 260ms cubic-bezier(.2,.9,.2,1)",
  },
  pulseDot: {
    position: "absolute",
    right: 2,
    top: 2,
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#FF5149",
    boxShadow: "0 0 0 0 rgba(255,81,73,0.6)",
    animation: "$pulse 1.4s infinite",
    border: "2px solid white",
  },
  silentDot: {
    position: "absolute",
    right: 4,
    top: 4,
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#A06DFF",
    border: "2px solid white",
  },
  countBadge: {
    position: "absolute",
    right: -6,
    top: -6,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    background: "#2B144D",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    border: "2px solid white",
  },
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(255,81,73,0.6)" },
    "70%": { boxShadow: "0 0 0 12px rgba(255,81,73,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(255,81,73,0)" },
  },
  "@keyframes ring": {
    "0%": { transform: "rotate(0)" },
    "10%": { transform: "rotate(25deg)" },
    "20%": { transform: "rotate(-20deg)" },
    "30%": { transform: "rotate(15deg)" },
    "40%": { transform: "rotate(-10deg)" },
    "50%": { transform: "rotate(5deg)" },
    "60%,100%": { transform: "rotate(0)" },
  },
  "@keyframes pop": {
    "0%": { transform: "scale(0.86)" },
    "60%": { transform: "scale(1.12)" },
    "100%": { transform: "scale(1)" },
  },
}));

export default function AnimatedNotificationIcon({
  hasNotification = true,
  count = 0,
  size = 40,
  onClick = () => {},
  title = "Notifications",
}) {
  const classes = useStyles();
  const [ringing, setRinging] = useState(false);
  const [popping, setPopping] = useState(false);
  // `seen` tracks whether the user has interacted (clicked) the icon since the last notification.
  const [seen, setSeen] = useState(false);

  // When a new notification arrives (hasNotification turns true), we reset `seen` so animations can play.
  useEffect(() => {
    if (hasNotification) {
      setSeen(false);

      // start pop + ring only if not already seen
      setPopping(true);
      const popTimer = setTimeout(() => setPopping(false), 260);

      setRinging(true);
      const ringTimer = setTimeout(() => setRinging(false), 800);

      return () => {
        clearTimeout(popTimer);
        clearTimeout(ringTimer);
        setPopping(false);
        setRinging(false);
      };
    } else {
      // if there are no notifications, ensure animations are off
      setPopping(false);
      setRinging(false);
    }
  }, [hasNotification]);

  // click handler: stop animations, mark as seen, and call external onClick
  const handleClick = useCallback(
    (e) => {
      // stop local animations and stop blinking
      setPopping(false);
      setRinging(false);
      setSeen(true);

      if (typeof onClick === "function") onClick(e);
    },
    [onClick]
  );

  const bellClasses = [classes.bell];
  if (popping && !seen) bellClasses.push(classes.popping);
  if (ringing && !seen) bellClasses.push(classes.ringing);

  return (
    <div className={classes.root} style={{ width: size, height: size }}>
      <Tooltip title={title}>
        <IconButton
          className={classes.iconButton}
          onClick={handleClick}
          style={{ width: size, height: size }}
        >
          <svg
            className={bellClasses.join(" ")}
            viewBox="0 0 24 24"
            width={size * 0.8}
            height={size * 0.8}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22c1.1 0 1.99-.9 1.99-2H10.01c0 1.1.89 2 1.99 2z"
              fill="#2B144D"
            />
            <path
              d="M18 16v-5c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 10-3 0v.68C7.63 5.36 6 7.92 6 11v5l-1.99 2H20L18 16z"
              fill="#A06DFF"
            />
            <path
              d="M12 6a4 4 0 00-4 4v4l-1 1h10l-1-1v-4a4 4 0 00-4-4z"
              fill="#FFFFFF"
              opacity="0.08"
            />
          </svg>
        </IconButton>
      </Tooltip>

      {/* Show pulsing dot only when there's an unseen notification */}
      {hasNotification && !seen ? (
        <span className={classes.pulseDot} />
      ) : (
        <span
        // className={classes.silentDot}
        />
      )}

      {count > 0 && (
        <div className={classes.countBadge}>{count > 99 ? "99+" : count}</div>
      )}
    </div>
  );
}
